import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { IActivity } from '@/activity-orchestration/types';

/** 活动唯一事实源；失效后队列与挂件会自动不可见。 */

/** 超出浏览器安全延迟时，由渲染与前台校时兜底。 */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** 队列和挂件共用的展示条件。 */
export const isActivityShowable = (activity: IActivity | undefined, now: number = Date.now()): boolean => {
    if (!activity) {
        return false;
    }
    if (activity.status !== 'active') {
        return false;
    }
    if (activity.expireAt != null && activity.expireAt <= now) {
        return false;
    }
    return true;
};

interface IActivityState {
    /** activityId -> IActivity（唯一副本） */
    activities: Record<string, IActivity>;
}

interface IActivityActions {
    /**
     * 写入 / 更新活动（transform 先调此，再 enqueue / upsertSession）。
     * expireAt 为 number 时挂过期定时器（§6.7 时机 1）；为 undefined 时不挂定时器、不兜底 TTL，
     * 由业务 / renderer 自行决定关闭与失效（见 activity-orchestration README checklist）。
     */
    upsertActivity: (activity: IActivity) => void;
    /** 按 activityId 解析活动数据（渲染 / 过期 / 复弹统一入口） */
    getActivity: (activityId: string) => IActivity | undefined;
    /** 置为 expired（过期定时器 / 渲染校验 / 可见性校时触发，§6.7） */
    expireActivity: (activityId: string) => void;
    /** 彻底移除活动并清理其定时器 */
    removeActivity: (activityId: string) => void;
    /** 硬导航 / 重置：清空所有活动与定时器 */
    reset: () => void;
}

/** 活动更新、移除或过期时必须清除旧定时器。 */
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearExpiryTimer = (activityId: string): void => {
    const timer = expiryTimers.get(activityId);
    if (timer) {
        clearTimeout(timer);
        expiryTimers.delete(activityId);
    }
};

const clearAllExpiryTimers = (): void => {
    expiryTimers.forEach(timer => clearTimeout(timer));
    expiryTimers.clear();
};

const initialState: IActivityState = {
    activities: {},
};

export const useActivityStore = create<IActivityState & IActivityActions>()(
    immer((set, get) => {
        /** 先清旧定时器，避免活动更新后提前失效。 */
        const scheduleExpiry = (activityId: string, expireAt: number): void => {
            clearExpiryTimer(activityId);
            const delay = expireAt - Date.now();
            // 避免在 upsert 内同步递归更新。
            if (delay <= 0) {
                queueMicrotask(() => get().expireActivity(activityId));
                return;
            }
            // 避免 setTimeout 溢出。
            if (delay > MAX_TIMEOUT_MS) {
                return;
            }
            const timer = setTimeout(() => {
                expiryTimers.delete(activityId);
                get().expireActivity(activityId);
            }, delay);
            expiryTimers.set(activityId, timer);
        };

        return {
            ...initialState,

            upsertActivity: activity => {
                const now = Date.now();
                const expireAt = activity.expireAt;
                set(state => {
                    const prev = state.activities[activity.activityId];
                    state.activities[activity.activityId] = {
                        ...prev,
                        ...activity,
                        expireAt,
                        status: activity.status ?? prev?.status ?? 'active',
                        // 创建顺序用于稳定排序，更新活动时不能重置。
                        createdAt: prev?.createdAt ?? activity.createdAt ?? now,
                    };
                });
                if (typeof expireAt === 'number') {
                    scheduleExpiry(activity.activityId, expireAt);
                } else {
                    clearExpiryTimer(activity.activityId);
                }
            },

            getActivity: activityId => get().activities[activityId],

            expireActivity: activityId => {
                clearExpiryTimer(activityId);
                set(state => {
                    const activity = state.activities[activityId];
                    if (activity) {
                        activity.status = 'expired';
                    }
                });
            },

            removeActivity: activityId => {
                clearExpiryTimer(activityId);
                set(state => {
                    delete state.activities[activityId];
                });
            },

            reset: () => {
                clearAllExpiryTimers();
                set(() => ({ ...initialState }));
            },
        };
    })
);
